/**
 * El turno del personal, capturado como turno y no como frase (CB-24).
 *
 * Reemplaza al `<input>` de texto libre en el que se escribía «lunes a viernes,
 * 6 a.m.-2 p.m.». Aquella frase servía para que un humano la leyera; de acá
 * sale un dato con el que se puede calcular la capacidad del día, que es lo que
 * el programador de visitas necesita.
 *
 * ── Por qué la capacidad se ve mientras se edita ────────────────────────────
 * Porque es el único punto del formulario donde se nota si el horario está
 * bien. «05:00 a 17:00 con desayuno y almuerzo» no dice nada; «10 h · 40
 * consultorios» sí. El número sale de `shiftCapacity` de `@cleanblue/shared`,
 * exactamente la misma función que usa el Worker al guardar — no es una cuenta
 * de la pantalla, es la cuenta del dominio traída acá.
 *
 * ── Los descansos son FIJOS, y aun así son datos ────────────────────────────
 * «Fijos» (P4.4) significa que no se mueven solos según la carga del día: el
 * desayuno y el almuerzo son bloques que el programador respeta, no huecos que
 * se acomodan. **No significa incrustados en el código**: se agregan, se
 * editan y se borran uno por uno, y el día que el dueño los mueva se mueven
 * acá, sin tocar nada.
 *
 * Por eso debajo de cada día se muestran los ratos que quedan para limpiar
 * (`cleanableWindows`). Es la forma de ver el descanso como lo que es —un
 * pedazo del día que dejó de existir para el programador— y no como una resta
 * que hay que creerle a la pantalla.
 */

import {
  BREAK_KIND_LABEL, cleanableWindows, durationLabel, findShiftProblems, minutesOfDay,
  SERVICE_WINDOW_END, SERVICE_WINDOW_START, shiftCapacity, timeOfMinutes, weeklyMinutes,
  WEEKDAY_NAME, windowsLabel,
  type BreakKind, type WorkShift,
} from '@cleanblue/shared';
import { Button } from '@/components/ui';

export type ShiftDraft = WorkShift;

/* Lunes primero: Clean Blue opera de lunes a viernes, y el fin de semana es la
   excepción. Poner el domingo arriba —como lo guarda la base— obligaría a
   saltearlo todas las veces. */
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function ScheduleEditor({ shifts, onChange, disabled }: {
  shifts: ShiftDraft[];
  onChange: (next: ShiftDraft[]) => void;
  disabled?: boolean;
}) {
  const problems = findShiftProblems(shifts);
  const total = weeklyMinutes(shifts);

  function replaceDay(weekday: number, next: ShiftDraft[]) {
    onChange([...shifts.filter((s) => s.weekday !== weekday), ...next]
      .sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time)));
  }

  function toggleDay(weekday: number, on: boolean) {
    if (!on) {
      replaceDay(weekday, []);
      return;
    }
    /* Al prender un día se copia el primer día ya declarado. Es lo que se
       quiere el 90 % de las veces —el horario del martes es el del lunes— y
       evita que la persona vuelva a teclear los descansos cinco veces. Si no
       hay ninguno todavía, se abre con la ventana de servicio del negocio, que
       es el único rango que no es una invención de esta pantalla. */
    const model = shifts[0];
    replaceDay(weekday, [model
      ? { ...model, weekday, breaks: model.breaks.map((b) => ({ ...b })) }
      : { weekday, start_time: SERVICE_WINDOW_START, end_time: SERVICE_WINDOW_END, breaks: [] }]);
  }

  function copyToWeekdays(from: ShiftDraft) {
    // Solo a los días que YA están prendidos: prender días por su cuenta sería
    // inventar jornadas que nadie declaró.
    const active = new Set(shifts.map((s) => s.weekday));
    active.delete(from.weekday);
    onChange([
      from,
      ...[...active].map((weekday) => ({
        ...from, weekday, breaks: from.breaks.map((b) => ({ ...b })),
      })),
    ].sort((a, b) => a.weekday - b.weekday));
  }

  return (
    <div>
      <div className="shift-week">
        {DISPLAY_ORDER.map((weekday) => {
          const dayShifts = shifts.filter((s) => s.weekday === weekday);
          const on = dayShifts.length > 0;
          const dayProblems = problems.filter((p) => p.weekday === weekday);
          const minutes = dayShifts.reduce((a, s) => a + shiftCapacity(s).net_minutes, 0);
          const visits = dayShifts.reduce((a, s) => a + shiftCapacity(s).visits, 0);

          return (
            <div key={weekday} className={`shift-day${on ? '' : ' shift-day--off'}`}>
              <div className="shift-day__head">
                <label className="shift-day__name">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={disabled}
                    onChange={(e) => toggleDay(weekday, e.target.checked)}
                  />
                  {WEEKDAY_NAME[weekday]}
                </label>

                {on && (
                  <div className="shift-day__times">
                    <input
                      className="input" type="time" aria-label={`Entrada del ${WEEKDAY_NAME[weekday]}`}
                      value={dayShifts[0]!.start_time} disabled={disabled}
                      onChange={(e) => replaceDay(weekday,
                        dayShifts.map((s, i) => (i === 0 ? { ...s, start_time: e.target.value } : s)))}
                    />
                    <span>a</span>
                    <input
                      className="input" type="time" aria-label={`Salida del ${WEEKDAY_NAME[weekday]}`}
                      value={dayShifts[0]!.end_time} disabled={disabled}
                      onChange={(e) => replaceDay(weekday,
                        dayShifts.map((s, i) => (i === 0 ? { ...s, end_time: e.target.value } : s)))}
                    />
                  </div>
                )}

                {on && (
                  <div className="shift-day__cap">
                    {durationLabel(minutes)}
                    <span>{visits} consultorio{visits === 1 ? '' : 's'}</span>
                  </div>
                )}
              </div>

              {on && (
                <div className="shift-day__body">
                  {dayShifts[0]!.breaks.map((brk, index) => (
                    /* La clave es el índice porque un descanso no tiene id
                       mientras se edita —lo recibe recién al guardarse— y
                       todos los campos de la fila son controlados: React no
                       tiene estado propio que se pueda desalinear al borrar
                       uno del medio. */
                    <div className="shift-break" key={`${weekday}-${index}`}>
                      <select
                        className="select" value={brk.kind} disabled={disabled}
                        aria-label="Tipo de descanso"
                        onChange={(e) => replaceDay(weekday, dayShifts.map((s, i) => (i === 0
                          ? {
                            ...s,
                            breaks: s.breaks.map((b, j) => (j === index
                              ? { ...b, kind: e.target.value as BreakKind } : b)),
                          }
                          : s)))}
                      >
                        {Object.entries(BREAK_KIND_LABEL).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                      <input
                        className="input" type="time" aria-label="Inicio del descanso"
                        value={brk.start_time} disabled={disabled}
                        onChange={(e) => replaceDay(weekday, dayShifts.map((s, i) => (i === 0
                          ? {
                            ...s,
                            breaks: s.breaks.map((b, j) => (j === index
                              ? { ...b, start_time: e.target.value } : b)),
                          }
                          : s)))}
                      />
                      <span>a</span>
                      <input
                        className="input" type="time" aria-label="Fin del descanso"
                        value={brk.end_time} disabled={disabled}
                        onChange={(e) => replaceDay(weekday, dayShifts.map((s, i) => (i === 0
                          ? {
                            ...s,
                            breaks: s.breaks.map((b, j) => (j === index
                              ? { ...b, end_time: e.target.value } : b)),
                          }
                          : s)))}
                      />
                      <Button
                        variant="ghost" disabled={disabled}
                        aria-label={`Quitar el ${BREAK_KIND_LABEL[brk.kind].toLowerCase()} del ${WEEKDAY_NAME[weekday]}`}
                        onClick={() => replaceDay(weekday, dayShifts.map((s, i) => (i === 0
                          ? { ...s, breaks: s.breaks.filter((_, j) => j !== index) }
                          : s)))}
                      >
                        Quitar
                      </Button>
                    </div>
                  ))}

                  {dayProblems.map((p) => (
                    <div className="shift-day__problem" key={p.code}>{p.message}</div>
                  ))}

                  {/* Los ratos que de verdad quedan para limpiar. Se muestra
                      solo cuando hay descansos o cuando el turno se sale de la
                      ventana de servicio: si el rato libre es el turno entero,
                      repetirlo sería ruido. */}
                  {dayShifts[0]!.breaks.length > 0 && dayProblems.length === 0 && (
                    <div className="shift-day__free">
                      Limpia {windowsLabel(cleanableWindows(dayShifts[0]!))}
                    </div>
                  )}

                  <div className="row row--wrap" style={{ gap: 6 }}>
                    <Button
                      variant="ghost" disabled={disabled}
                      onClick={() => replaceDay(weekday, dayShifts.map((s, i) => (i === 0
                        ? { ...s, breaks: [...s.breaks, nextBreak(s)] } : s)))}
                    >
                      + Descanso
                    </Button>
                    {shifts.length > 1 && (
                      <Button
                        variant="ghost" disabled={disabled}
                        onClick={() => copyToWeekdays(dayShifts[0]!)}
                      >
                        Copiar a los otros días
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="shift-total">
        <span className="muted">Capacidad semanal, calculada</span>
        <strong>{durationLabel(total)}</strong>
      </div>
    </div>
  );
}

/**
 * Un descanso nuevo, ya adentro del turno.
 *
 * Nace válido a propósito: se propone **cinco horas después de entrar** y
 * siempre recortado al turno, así agregar un descanso nunca produce un error
 * que la persona tenga que ir a arreglar. Cinco horas y no cuatro porque el
 * turno real arranca a las 05:00 y P4.4 corrigió el desayuno a las 10:00; con
 * cuatro, el formulario proponía el 09:00 viejo y había que corregirlo a mano
 * todas las veces.
 *
 * Sigue siendo un punto de partida y no una regla: se mueve ahí mismo, y el
 * día que el desayuno cambie de hora esto solo queda proponiendo de más, no
 * imponiendo de menos.
 */
function nextBreak(shift: ShiftDraft): { kind: BreakKind; start_time: string; end_time: string } {
  const from = minutesOfDay(shift.start_time);
  const to = minutesOfDay(shift.end_time);
  const last = shift.breaks.reduce((m, b) => Math.max(m, minutesOfDay(b.end_time)), from);
  const start = Math.min(Math.max(last, from + 300), Math.max(from, to - 60));
  const end = Math.min(start + 60, to);
  const kind: BreakKind = shift.breaks.length === 0
    ? 'BREAKFAST'
    : (shift.breaks.some((b) => b.kind === 'LUNCH') ? 'BREAK' : 'LUNCH');
  return { kind, start_time: timeOfMinutes(start), end_time: timeOfMinutes(Math.max(end, start + 1)) };
}

/** ¿Se puede guardar? Lo usa el modal para apagar el botón. */
export function scheduleIsValid(shifts: ShiftDraft[]): boolean {
  return findShiftProblems(shifts).length === 0;
}
